/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { ACTIONS } from '@airasia-rn/ui';

import {
  ANALYTICS_CONSTANTS,
  AnalyticsBuilderResult,
  CATEGORY,
  EVENT_NAMES,
  EVENTS,
  FlightSearchClickParams,
  FlightSearchParams,
  FlightSearchParamsWithSearch,
  SCREEN_NAME,
} from './flightSearch.meta';

export const buildCreateTripAnalytics = (
  params: FlightSearchParams,
): AnalyticsBuilderResult => {
  const nativeParams = {
    label: `Create New Trip|${params.label}`,
    screenName: SCREEN_NAME.flightSearchWidget,
    category: CATEGORY.projectLink,
    action: ACTIONS.tap,
  };
  const webParams = {
    'eventDetails.label': `Create New Trip|${params.label}`,
    'eventDetails.category': CATEGORY.projectLink,
    event: EVENTS.trackEventNoEcommerce,
    'eventDetails.action': ACTIONS.view,
  };

  return {
    nativeParams,
    webParams,
  };
};

export const buildClickSearchAnalytics = (
  params: FlightSearchParams,
): AnalyticsBuilderResult => {
  const nativeParams = {
    label: `${params.label}`,
    screenName: SCREEN_NAME.flightSearchWidget,
    category: CATEGORY.flightSearchWidget,
    action: ACTIONS.tap,
  };
  const webParams = {
    'eventDetails.label': `${params.label}`,
    'eventDetails.category': CATEGORY.flightSearchWidget,
    event: EVENTS.trackEventNoEcommerce,
    'eventDetails.action': ACTIONS.click,
  };

  return {
    nativeParams,
    webParams,
  };
};

export const buildDateSelectionAnalytics = (
  params: FlightSearchParams,
): AnalyticsBuilderResult => {
  const nativeParams = {
    event: EVENTS.trackEventNoEcommerce,
    label: params.label,
    screenName: SCREEN_NAME.flightSearchWidget,
    category: params.category,
    action: ACTIONS.tap,
    lineofbusiness: ANALYTICS_CONSTANTS.FLIGHT,
  };
  const webParams = {
    eventLabel: params.label,
    eventCategory: params.category,
    event: EVENTS.trackEventNoEcommerce,
    eventAction: ACTIONS.click,
    lineofbusiness: ANALYTICS_CONSTANTS.FLIGHT,
    screenName: SCREEN_NAME.flightSearchWidget,
  };

  return {
    nativeParams,
    webParams,
  };
};

export const buildStationSelectionAnalytics = (
  params: FlightSearchParams,
): AnalyticsBuilderResult => {
  const nativeParams = {
    label: params.label,
    screenName: SCREEN_NAME.flightSearchWidget,
    category: params.category,
    lineofbusiness: ANALYTICS_CONSTANTS.FLIGHT,
  };
  const webParams = {
    eventLabel: params.label,
    eventCategory: params.category,
    event: EVENTS.trackEventNoEcommerce,
    eventAction: ACTIONS.click,
    lineofbusiness: ANALYTICS_CONSTANTS.FLIGHT,
    screenName: SCREEN_NAME.flightSearchWidget,
  };

  return {
    nativeParams,
    webParams,
  };
};

export const buildFlightSearchClickAnalytics = (
  params: FlightSearchClickParams,
): AnalyticsBuilderResult => {
  const sharedParams = {
    screenName: SCREEN_NAME.home,
    lineofbusiness: ANALYTICS_CONSTANTS.FLIGHT,
    ...params,
  };

  const nativeParams = sharedParams;
  const webParams = {
    event: EVENTS.trackEventNoEcommerce,
    eventAction: EVENTS.productImpressionSearch,
    ...sharedParams,
  };

  return { nativeParams, webParams };
};

export const buildSearchWidgetViewedAnalytics = (): AnalyticsBuilderResult => {
  const nativeParams = {
    category: ANALYTICS_CONSTANTS.FLIGHT_SEARCH_WIDGET,
    label: 'flight search widget exposed',
    screenName: SCREEN_NAME.home,
    lineofbusiness: ANALYTICS_CONSTANTS.FLIGHT,
  };
  const webParams = {
    event: EVENTS.trackEventNoEcommerce,
    eventCategory: ANALYTICS_CONSTANTS.FLIGHT_SEARCH_WIDGET,
    eventAction: EVENT_NAMES.searchWidgetViewed,
    eventLabel: 'flight search widget exposed',
    screenName: SCREEN_NAME.home,
    lineofbusiness: ANALYTICS_CONSTANTS.FLIGHT,
  };

  return { nativeParams, webParams };
};

export const buildSearchAnalytics = (
  params: FlightSearchParamsWithSearch,
): AnalyticsBuilderResult => {
  const nativeParams = {
    label: params.label,
    event: EVENTS.productImpressionSearch,
    screenName: ANALYTICS_CONSTANTS.HOME,
    category: params.category,
    action: ACTIONS.tap,
    lineofbusiness: ANALYTICS_CONSTANTS.FLIGHTS,
    ...params.searchParams,
  };
  const webParams = {
    'eventDetails.label': params.label,
    'eventDetails.category': params.category,
    event: EVENTS.productImpressionSearch,
    'eventDetails.action': ACTIONS.view,
    lineofbusiness: ANALYTICS_CONSTANTS.FLIGHTS,
    ...params.searchParams,
  };

  return {
    nativeParams,
    webParams,
  };
};
